import {
  Box,
  Button,
  FormHelperText,
  Grid,
  makeStyles,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import TextField from "@material-ui/core/TextField";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTable } from "react-table";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { Menu } from "@material-ui/icons";
import { Link } from "react-router-dom";

import apiHelper from "../../../../util/ApiHelper";
import { formatSubRows } from "../PeriodListingPage";
import { toast } from "react-toastify";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;

const useStyles = makeStyles({
  root: {
    marginBottom: "40px",
  },
  marginBottom: {
    marginBottom: "20px",
  },
  button: {
    backgroundColor: "#306F9D",
    textTransform: "capitalize",
    color: "white",
    "&:hover": {
      backgroundColor: "#306F9D",
    },
  },
  input: {
    width: "100%",
  },
  permalink: {
    fontSize: "14px",
    marginBottom: "40px",
    color: "#333333",

    "& .link": {
      display: "inline",
      color: "#306F9D",
      margin: "0 10px",
      "& input": {
        padding: "7px 14px",
        fontSize: "13px",
      },
    },
    "& button": {
      padding: "2px 20px",
      textTransform: "inherit",
      color: "#727272",
    },
  },
});

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 12, paddingBottom: 12, color: "#306F9D" }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

class DraggableTableRow extends React.Component {
  render() {
    const { snapshot, row, provided, className } = this.props;
    return (
      <IsDraggingContext.Consumer>
        {(isDragging) => (
          <TableRow
            className={className}
            ref={provided.innerRef}
            isDragging={snapshot.isDragging}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <DraggableTableCell
              isDragOccurring={isDragging}
              component="th"
              scope="row"
            >
              <Link
                to={`/cinefan/period/edit/${row.id}`}
                style={{ color: "#306F9D", textDecoration: "none" }}
              >
                {row.enName}
              </Link>
            </DraggableTableCell>
            <DraggableTableCell isDragOccurring={isDragging} align="left">
              {row.tcName}
            </DraggableTableCell>
            <DraggableTableCell isDragOccurring={isDragging} align="left">
              <span style={{ color: "#333333" }}>{row.slug}</span>
            </DraggableTableCell>
            <DraggableTableCell isDragOccurring={isDragging} align="left">
              <span style={{ color: "#333333" }}>{row.count}</span>
            </DraggableTableCell>
            <DraggableTableCell isDragOccurring={isDragging} align="right">
              <Menu fontSize="small" style={{ color: "#CCCCCC" }} />
            </DraggableTableCell>
          </TableRow>
        )}
      </IsDraggingContext.Consumer>
    );
  }
}

function EditPeriodPage({ disabled, ...props }) {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
  } = useForm();

  const { history } = props;
  const id = props.match.params.id;
  const classes = useStyles();
  const [isDragging, setIsDragging] = React.useState(false);
  const [parents, setParents] = React.useState([]);
  const [parentPeriod, setParentPeriod] = React.useState(null);
  const [periodStatus, setPeriodStatus] = React.useState("active");
  const [data, setData] = React.useState(null);
  const [childPeriod, setChildPeriod] = React.useState(null);
  const [clonedPermalink, setClonedPermalink] = React.useState("");
  const [isSlugInvalid, setIsSlugInvalid] = React.useState(false);
  const [isEditPermalink, setEditPermalink] = React.useState(false);

  useEffect(() => {
    getDetail();

    (async () => {
      apiHelper.cinefan.getParentPeriodList().then((response) => {
        const periods = response.data.sort((a, b) => a.sequence - b.sequence);
        setParents(periods);
      });
    })();
  }, []);

  const getDetail = React.useCallback(async () => {
    try {
      const result = await apiHelper.cinefan.getPeriodById(id);
      console.log(result.data);
      const details = result.data;

      if (details.childPeriod) {
        details.childPeriod = formatSubRows(details.childPeriod);
        setChildPeriod(details.childPeriod);
      }

      setData(details);
      setClonedPermalink(details.slug);
      setPeriodStatus(details.archive);
      setParentPeriod(details.parentId);
    } catch (err) {
      console.log(err);
    }
  }, [id]);

  const columns = React.useMemo(
    () => [
      {
        Header: "Name (English)",
        accessor: "enName",
      },
      {
        Header: "Name (Traditional)",
        accessor: "tcName",
      },
      {
        Header: "Slug",
        accessor: "slug",
      },
      {
        Header: "Count",
        accessor: "count",
      },
      {
        Header: "",
        accessor: "order",
      },
    ],
    []
  );
  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: childPeriod || [],
  });
  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      childPeriod,
      result.source.index,
      result.destination.index
    );
    setChildPeriod(items);
  };

  const handleChange = (event) => {
    setParentPeriod(event.target.value);
  };

  const handleStatus = (event, newAlignment) => {
    setPeriodStatus(newAlignment);
  };

  const onSubmit = async (values) => {
    try {
      const childSequence = childPeriod.map((child, idx) => {
        return { id: child.id, sequence: idx + 1 };
      });

      const params = values;
      params.childSequence = childSequence;
      params.parentId =
        parentPeriod !== null && parentPeriod !== "" && parentPeriod !== 0
          ? Number(parentPeriod)
          : null;
      params.id = data.id;
      params.archive = periodStatus;
      console.log(params);
      if (!params.slug) {
        toast.error("Slug required.");
        return;
      }
      const result = await apiHelper.cinefan.updatePeriod(id, params);
      if (result.data.code >= 0) {
        toast.dark("Your changes is saved.");
      } else {
        toast.dark("Something went wrong, please try again later.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deletePeriod = async () => {
    try {
      console.log("deletePeriod");
      await apiHelper.cinefan.deletePeriod(id);
      history.push("/cinefan/period");
      toast.dark("The period has been deleted.");
    } catch (err) {
      console.log(err);
    }
  };

  return (
    data && (
      <div>
        <Grid container xs={12} md={8} lg={6}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <fieldset disabled={disabled} className="fieldset">
              <Typography variant="h4" gutterBottom>
                Programmes & Periods > Edit Period
              </Typography>
              <Typography variant="h6" gutterBottom>
                Edit Period
              </Typography>
              <Box
                display="flex"
                flexDirection="column"
                className={classes.root}
              >
                <Controller
                  name="localizations.en.name"
                  control={control}
                  defaultValue={data.localizations.en.name}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      margin="dense"
                      label="Name (English)"
                      variant="outlined"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={
                        error
                          ? error.message
                          : "The name is how it appears on your site."
                      }
                    />
                  )}
                  rules={{ required: "Name (English) required" }}
                />
                <Controller
                  name="localizations.tc.name"
                  control={control}
                  defaultValue={data.localizations.tc.name}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <TextField
                      margin="dense"
                      label="Name (Traditional Chinese)"
                      variant="outlined"
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={
                        error
                          ? error.message
                          : "The name is how it appears on your site."
                      }
                    />
                  )}
                  rules={{ required: "Name (Traditional Chinese) required" }}
                />
                {/* <Controller
                name="slug"
                control={control}
                defaultValue={data.slug}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    margin="dense"
                    label="Slug"
                    variant="outlined"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={
                      error
                        ? error.message
                        : "The “slug” is the URL-friendly version of the name. It is usually all lowercase and contains only letters, numbers, and hyphens."
                    }
                  />
                )}
                rules={{ required: "Menu Title (English) required" }}
              /> */}

              <div className={classes.permalink}>
                Permalink:
                <div className="link">
                  {(!isEditPermalink) && (
                    <>
                      {frontendUrl || "https://cinefan.com.hk"}/
                      {parentPeriod ? `${parentPeriod}/` : ""}
                      {getValues(`slug`)
                        ? getValues(`slug`)
                        : data.slug}
                      <Button
                        variant="outlined"
                        onClick={() => {
                            setEditPermalink(true);
                            setClonedPermalink(
                              getValues(`slug`) ? getValues(`slug`) : data.slug
                            );
                          }}
                        >
                          Edit
                        </Button>
                        <input
                          value={data.slug}
                          type="hidden"
                          {...register(`slug`)}
                        />
                      </>
                    )}
                    {isEditPermalink && (
                      <>
                        <span>{frontendUrl || "https://cinefan.com.hk"}/</span>
                        <Controller
                          name={`slug`}
                          control={control}
                          defaultValue={data.slug}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              variant="outlined"
                              margin="dense"
                              {...field}
                              error={isSlugInvalid}
                              helperText={isSlugInvalid && "slug required"}
                            />
                          )}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (getValues(`slug`)) {
                              setIsSlugInvalid(false);
                              setEditPermalink(false);
                            } else {
                              setIsSlugInvalid(true);
                            }
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => {
                            setValue(`slug`, clonedPermalink);
                            setEditPermalink(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {data.childPeriod.length === 0 && (
                  <TextField
                    id="standard-select-parentPeriod"
                    select
                    label="Parent Period"
                    value={parentPeriod}
                    onChange={handleChange}
                    helperText="Assign a parent term to create a hierarchy. The term Jazz, for example, would be the parent of Bebop and Big Band."
                    variant="outlined"
                    className={classes.input}
                    disabled={disabled}
                  >
                    {parents.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.slug}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                {data.childPeriod.length > 0 && (
                  <div className={classes.marginBottom}>
                    <IsDraggingContext.Provider value={isDragging}>
                      <Typography variant="h6" gutterBottom>
                        Child Period Ordering
                      </Typography>
                      <Paper>
                        <Table
                          aria-label="simple table"
                          component="ul"
                          {...getTableProps()}
                        >
                          <TableHead component="div">
                            {headerGroups.map((headerGroup) => (
                              <TableRow {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                  <TableCell
                                    {...column.getHeaderProps()}
                                    style={{
                                      paddingTop: 12,
                                      paddingBottom: 12,
                                    }}
                                  >
                                    {column.render("Header")}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableHead>
                          <DragDropContext
                            onBeforeDragStart={onBeforeDragStart}
                            onDragEnd={onDragEnd}
                          >
                            <Droppable droppableId="droppable" type="QUESTIONS">
                              {(provided, snapshot) => (
                                <TableBody
                                  component="div"
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                >
                                  {rows.map((row, index) => {
                                    prepareRow(row);
                                    return (
                                      <Draggable
                                        key={row.id}
                                        draggableId={row.id}
                                        index={index}
                                        isDragDisabled={disabled}
                                      >
                                        {(provided, snapshot) => (
                                          <DraggableTableRow
                                            key={row.id}
                                            row={row.original}
                                            provided={provided}
                                            snapshot={snapshot}
                                          />
                                        )}
                                      </Draggable>
                                    );
                                  })}
                                  {provided.placeholder}
                                </TableBody>
                              )}
                            </Droppable>
                          </DragDropContext>
                        </Table>
                      </Paper>
                    </IsDraggingContext.Provider>
                  </div>
                )}
                <Grid container xs="8">
                  <ToggleButtonGroup
                    value={periodStatus}
                    exclusive
                    onChange={handleStatus}
                    aria-label="period status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value="CURRENT"
                      aria-label="active"
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value="ARCHIVE"
                      aria-label="archive"
                      disabled={disabled}
                    >
                      Archive
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <FormHelperText className={classes.margin}>
                    A status for the period is where it appears on your site.
                  </FormHelperText>
                </Grid>
              </Box>

              <Button
                className={classes.button}
                type="submit"
                variant="contained"
                color="primary"
              >
                Save Changes
              </Button>
              <Button color="secondary" onClick={deletePeriod}>
                Delete Period
              </Button>
            </fieldset>
          </form>
        </Grid>
      </div>
    )
  );
}

export default EditPeriodPage;
